import { defineStore } from "pinia";
import { useSurveyStore } from "./survey";
import { computed, ref, watch } from "vue";
import { getLanguageOptions } from "@/utils/constants/languages";
import { isEmpty } from "@/utils/toolBox";
import { useDefaultMessagesStore } from "./defaultMessages";

export const useSurveyLocalesStore = defineStore("surveyLocales", () => {
  const parentStore = useSurveyStore();
  const defaultMessagesStore = useDefaultMessagesStore();

  // state
  const currentLocale = ref(null);

  // actions
  const localizeText = (data) => data?.[currentLocale.value?.token] ?? "";
  const localizeTextWithAnalysisFallback = (data) => {
    const text = data?.[currentLocale.value?.token];
    if (text) return text;
    return data?.[analysisLocale.value?.token] ?? "";
  };
  const updateLocale = (token) => {
    currentLocale.value = localeByIds.value?.[token];
    defaultMessagesStore.fetch(currentLocale.value?.language);
  };
  const defineLocaleForAnalysis = () => {
    updateLocale(analysisLocale.value?.token || defaultLocale.value?.token);
  };
  const labelizeLocale = (text) => {
    if (!text) return "";

    var newText = text.toString();
    localeOptions.value.forEach((option) => (newText = newText.replaceAll(option.value, option.label)));
    return newText;
  };

  // getters
  const locales = computed(() => parentStore?.survey?.locales ?? []);
  const localeByIds = computed(() => parentStore?.survey?.locale_by_ids ?? {});
  const localesTokens = computed(() => locales.value.map(({ token }) => token));
  const isMultiLocale = computed(() => locales.value.length > 1);
  const defaultLocale = computed(() => (locales.value.length ? locales.value[0] : null));
  const analysisLocale = computed(() => {
    const locale = locales.value.find((locale) => locale.language === parentStore?.survey?.analysis_language);
    if (locale === undefined) return defaultLocale.value;

    return locale;
  });
  const localeModel = computed(() => currentLocale.value?.token ?? localeOptions.value?.[0]?.value);
  const isAnalysisLocale = computed(() => currentLocale.value?.token === analysisLocale.value?.token);

  const localeOptions = computed(() => {
    const languages = getLanguageOptions();
    return locales.value.map((locale) => {
      let label = languages[locale.language]?.label;
      if (!isEmpty(locale?.variant)) label += ` - ${locale?.variant}`;
      return {
        label: `${label}`,
        value: `${locale?.token}`,
        name: `${locale?.token}`,
        language: `${locale?.language}`,
      };
    });
  });

  const getLocaleOptionsOnSurvey = (locales) => {
    const languages = getLanguageOptions();

    return locales.value?.map((locale) => {
      let label = Object.entries(languages).filter(([_key, value]) => value.value === locale[1].language)[0][1].label;

      if (!isEmpty(locale[1].variant)) label += ` - ${locale[1].variant}`;
      return {
        label: label,
        value: locale[0],
        name: locale[0],
      };
    });
  };

  watch(locales, () => {
    const localeNotSet = !currentLocale.value;
    const localeBelongsToDifferentSurvey = !localeByIds.value?.[currentLocale.value?.token];

    if (localeNotSet || localeBelongsToDifferentSurvey) defineLocaleForAnalysis();
  }, { immediate: true });
  
  return {
    // state
    currentLocale,
    // actions
    defineLocaleForAnalysis,
    updateLocale,
    localizeText,
    localizeTextWithAnalysisFallback,
    labelizeLocale,
    getLocaleOptionsOnSurvey,
    // getters
    locales,
    localeByIds,
    localeModel,
    defaultLocale,
    analysisLocale,
    localeOptions,
    localesTokens,
    isMultiLocale,
    isAnalysisLocale,
  };
});
